/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {
  ResolveOptions,
  ResolveValue,
  resolveValue as dlsResolveValue
} from '@ion/dls/src/utils/ComponentUtils';

// [ION-BMW-PORTAL TODO]: ADD RESOLVE CORRESPONDING VALUE UNIT TESTS ...
/**
 * Provides a wrapper around the DLS resolveValue utility.
 *
 * ```typescript
 *
 *   import {resolveValue} from '@/utils';
 *
 *   const resolved = resolveValue('');
 *   // '-'
 *
 *   const resolved = resolveValue(null, {resolver: translate('common:unavailable')});
 *   // 'Unavailable'
 *
 *   const resolved = resolveValue(new Date('...'), {transformer: calendarDate});
 *   // '09/25/20'
 *
 * ```
 *
 */
export const resolveValue = (value: ResolveValue, options?: ResolveOptions): string => {
  const opts = {
    resolver : '-',
    ...options
  };
  return dlsResolveValue(value, opts);
};

/**
 * Checks the specified label container for overflow.
 *
 * ```typescript
 *
 *   import {checkOverflow} from '@/utils';
 *
 *   const overflowed = checkOverflow(label);
 *
 * ```
 *
 */
export function checkOverflow(label) {
  return !isNil(label) ? (label.clientWidth >= label.scrollWidth) : false;
}

/**
 * Converts the specified datastring to object.
 *
 * ```typescript
 *
 *   import {parseStringToObject} from '@/utils';
 *
 *   const address = parseStringToObject('Address [street=Dc, city=Ranch]');
 *   \\ { street: 'Dc', city: 'Ranch' }
 * ```
 *
 */
export const parseStringToObject = (dataString: string) => {
  const address = {};
  if (!isEmpty(dataString)) {
    const value   = dataString.split(' [');
    value.shift();
    let content   = value.join(' [');
    content       = content.slice(0, content.length - 1);
    const labels  = content.split('=');
    let nextLabel = labels[0];
    labels.forEach((label, i) => {
      const part = label;
      const last = i === labels.length - 1;
      const split = part.split(', ');
      const currentLabel = split[split.length - 1];
      if (!last) {
        split.pop();
      }
      const value = split.join(', ');
      address[nextLabel] = value;
      nextLabel = currentLabel;
    });
  }

  return address;
};

/**
 * Resolves the default language from the query string.
 *
 * Examples:
 *   ?language=en_US  -> "en"
 *   ?language=fr-CA  -> "fr"
 *   ?language=de     -> "de"
 *
 * @returns The lowercase language code (e.g. "en") or null if not found.
 */
export const resolveDefaultLanguage = (): string | null => {
  const languageParam = getParameter('language') || null;
  return languageParam?.split(/[_-]/)?.[0].toLowerCase() || null;
};
